// Section · The six doctrinal pipelines.
//
// Source of truth: STRIKZA_DualSwarm_Architecture v1.0.0,
// STRIKZA_POWERBALL_Architecture_v1.3.docx, STRIKZA_LADDER_Architecture_PRD,
// STRIKZA_SINGLES_CHAIN_PRD_v2 + Multi-Sport Amendment, STRIKZA_Owl_Hour.
//
// Replaces the legacy 4-card PITCH/PULSE/BLOCK/PIP module roadmap
// (which was an asset-class expansion taxonomy, not the primary
// product roadmap). Phase-2 markets are still mentioned in the
// footer so the expansion thesis remains visible without
// overshadowing the sports-probability spine.

import { LATER_MARKETS, PIPELINES, STATUS_COLOR } from "./landing-data.ts";

export function ModulesRoadmap(): JSX.Element {
  return (
    <section
      className="sz-l-section"
      style={{
        padding: "160px 0",
        borderTop: "1px solid var(--border)",
        background: "linear-gradient(180deg, #0A0A0A 0%, #0C0B08 100%)",
      }}
    >
      <div className="container">
        <div
          className="sz-l-section-header"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.6fr",
            gap: 80,
            marginBottom: 60,
          }}
        >
          <div>
            <span className="bracket">Section · Pipelines</span>
          </div>
          <div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                lineHeight: 1.02,
                color: "var(--text)",
                marginBottom: 22,
                letterSpacing: "-0.01em",
              }}
            >
              Six pipelines.
              <br />
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>One engine.</em>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--text-muted)",
                maxWidth: 640,
                lineHeight: 1.55,
              }}
            >
              Daily slips through Claude. Weekend POWERBALL 10 and MEGA POWERBALL 20 through OpenAI.
              LADDER climbers, 7-day Singles Chain, and the late-night Owl Hour run the full
              18-agent pipeline. The base platform — account, ROLLOVER engine, Daily Intelligence
              Ledger — covers all six.
            </p>
          </div>
        </div>

        <div
          className="sz-l-modules-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
          }}
        >
          {PIPELINES.map((m) => (
            <div
              key={m.code}
              style={{
                background:
                  m.status === "live"
                    ? "linear-gradient(180deg, #1A1408 0%, #0E0E0E 100%)"
                    : "var(--surface)",
                border:
                  m.status === "live" ? "1px solid var(--accent-deep)" : "1px solid var(--border)",
                padding: "32px 28px",
                position: "relative",
                minHeight: 280,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Status pill */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontFamily: "var(--mono)",
                  fontSize: 9,
                  letterSpacing: "0.18em",
                  color: STATUS_COLOR[m.status],
                  textTransform: "uppercase",
                  fontWeight: 600,
                  marginBottom: 24,
                }}
              >
                {m.status === "live" && (
                  <span className="live-dot" style={{ width: 5, height: 5 }} />
                )}
                <span>· {m.statusLbl}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <div
                  className="display"
                  style={{
                    fontSize: 38,
                    color: m.status === "live" ? "var(--accent)" : "var(--text)",
                    letterSpacing: "0.02em",
                    lineHeight: 1,
                  }}
                >
                  {m.code}
                </div>
                <span style={{ fontSize: 22, color: "var(--text-muted)" }}>{m.icon}</span>
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  marginBottom: 20,
                }}
              >
                {m.market}
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  lineHeight: 1.55,
                  paddingTop: 18,
                  borderTop: "1px solid var(--border)",
                  flex: 1,
                }}
              >
                {m.why}
              </div>
            </div>
          ))}
        </div>

        {/* Phase-2 expansion strip — same engine, other markets. */}
        <div
          style={{
            marginTop: 48,
            padding: "24px 28px",
            border: "1px solid var(--border)",
            background: "rgba(17, 17, 17, 0.5)",
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--accent)",
              textTransform: "uppercase",
              marginBottom: 14,
            }}
          >
            Phase 2 · Same engine, other markets
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 16,
            }}
          >
            {LATER_MARKETS.map((m) => (
              <div
                key={m.code}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                  }}
                >
                  <span
                    className="display"
                    style={{ fontSize: 18, color: "var(--text)", letterSpacing: "0.01em" }}
                  >
                    {m.code}
                  </span>
                  <span
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: "var(--text-dim)",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    {m.market}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.45 }}>
                  {m.note}
                </div>
              </div>
            ))}
          </div>
        </div>

        <p
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--text-dim)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            marginTop: 24,
            textAlign: "right",
          }}
        >
          / Sports-probability spine first · asset-class expansion downstream
        </p>
      </div>
    </section>
  );
}
